/* eslint-disable no-unused-vars */
import { useEffect, useMemo, useState, useCallback, memo } from 'react';
import { Sidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getAdminuUerDetails } from '../store/Actions/AuthActions';
import { sidebarMenuConfig } from '../config/sidebarMenuConfig';
import { PrakurtiLogoWhite } from './imagepath';
import { useSidebar } from '../context/SidebarContext';

const SidebarN = memo(props => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobileOpen, isDesktopCollapsed, toggleMobileMenu, closeMobileMenu, toggleDesktopCollapse } = useSidebar();

  // Optimize selector to only trigger on relevant changes
  const userDetails = useSelector(state => state?.auth?.userDetails);
  const authError = useSelector(state => state?.auth?.error);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 992);
  const [menuLoading, setMenuLoading] = useState(false);

  // Memoize permissions as a Set for O(1) lookup
  const userPermissionsSet = useMemo(() => {
    const permissions = userDetails?.[0]?.role?.permission;
    return new Set(Array.isArray(permissions) ? permissions : []);
  }, [userDetails]);

  const hasPermission = useCallback((permissionString) => {
    if (!permissionString) return true;
    return userPermissionsSet.has(permissionString);
  }, [userPermissionsSet]);

  const isItemVisible = useCallback((item) => {
    // Permission check for the item itself
    if (item.permission) {
      const allowed = hasPermission(item.permission);
      if (item.invertPermission) {
        if (allowed) return false;
      } else {
        if (!allowed) return false;
      }
    }

    // If item has sub-items, check if at least one sub-item is visible
    if (item.subItems && item.subItems.length > 0) {
      return item.subItems.some(subItem => isItemVisible(subItem));
    }

    // If it's a leaf node and passed its own permission check, it's visible
    return true;
  }, [hasPermission]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(prev => (prev !== mobile ? mobile : prev));
      // Reset sidebar state on mobile breakpoint change
      if (mobile) {
        closeMobileMenu();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [closeMobileMenu]);

  useEffect(() => {
    if (!userDetails) {
      setMenuLoading(true);
      const promise = dispatch(getAdminuUerDetails());
      if (promise && typeof promise.finally === 'function') {
        promise.finally(() => setMenuLoading(false));
      } else {
        setMenuLoading(false);
      }
    }
  }, [dispatch, userDetails]);

  const handleNavigation = useCallback(
    path => {
      if (path && path !== '#') {
        navigate(path);
        // Close mobile sidebar after navigation
        if (isMobile) {
          closeMobileMenu();
        }
      }
    },
    [navigate, isMobile, closeMobileMenu]
  );

  const isItemActive = useCallback(path => {
    if (!path || path === '#') return false;
    const currentPath = location.pathname;
    const targetPath = path.split('?')[0];
    return currentPath === targetPath || currentPath.startsWith(targetPath + '/');
  }, [location.pathname]);

  const isSubMenuActive = useCallback(items => {
    if (!items) return false;
    return items.some(subItem => {
      if (isItemActive(subItem.path)) return true;
      if (subItem.subItems) return isSubMenuActive(subItem.subItems);
      return false;
    });
  }, [isItemActive]);

  // Memoize the entire menu structure to prevent re-calculating on every render
  const menuContent = useMemo(() => {
    const renderItems = (items, level = 0) => {
      if (menuLoading) return null;

      return items
        .filter(isItemVisible)
        .map((item, index) => {

          if (item.isSubmenu || (item.subItems && item.subItems.length > 0)) {
            const active = isSubMenuActive(item.subItems);
            return (
              <SubMenu
                key={item.id || item.label || index}
                icon={
                  item.icon ? (
                    <div className="menu-icon-wrapper">
                      <img src={item.icon} alt={item.label} style={{ width: 20, height: 20 }} />
                    </div>
                  ) : null
                }
                label={item.label}
                defaultOpen={active}
                className="sidebar-submenu"
              >
                {renderItems(item.subItems, level + 1)}
              </SubMenu>
            );
          }

          return (
            <MenuItem
              key={item.path || item.label || index}
              icon={
                item.icon ? (
                  <div className="menu-icon-wrapper">
                    <img src={item.icon} alt={item.label} style={{ width: 20, height: 20 }} />
                  </div>
                ) : null
              }
              onClick={() => handleNavigation(item.path)}
              active={isItemActive(item.path)}
              className="sidebar-menu-item"
            >
              <span className="menu-item-text">{item.label}</span>
            </MenuItem>
          );
        });
    };

    if (menuLoading) {
      return (
        <div className="sidebar-loading">
          <div className="loading-spinner"></div>
          <p>Loading menu...</p>
        </div>
      );
    }

    if (authError) {
      return (
        <div className="sidebar-error">
          <i className="fas fa-exclamation-triangle"></i>
          <p>Error loading menu</p>
        </div>
      );
    }

    return renderItems(sidebarMenuConfig);
  }, [menuLoading, authError, isItemVisible, isSubMenuActive, isItemActive, handleNavigation]);

  const sidebarStyles = useMemo(() => ({
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
    position: 'fixed',
    top: isMobile ? 0 : '75px',
    height: isMobile ? '100vh' : 'calc(100vh - 75px)',
    zIndex: 1040,
    borderRight: '1px solid #edf2f7',
    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    willChange: 'transform, box-shadow',
    ...(isMobile && {
      left: 0,
      width: '280px',
      borderRadius: 0,
      zIndex: 1100,
      boxShadow: isMobileOpen ? '0 0 40px rgba(0, 0, 0, 0.2)' : 'none',
      transform: isMobileOpen ? 'translateX(0) translateZ(0)' : 'translateX(-100%) translateZ(0)',
    }),
    ...(!isMobile && {
      transform: 'translateZ(0)',
    }),
  }), [isMobile, isMobileOpen]);

  const menuItemStyles = useMemo(() => ({
    button: ({ active, disabled }) => ({
      backgroundColor: active ? '#f0f7ff' : 'transparent',
      color: active ? '#2563eb' : '#475569',
      fontWeight: active ? '600' : '500',
      borderRadius: '12px',
      margin: isDesktopCollapsed ? '4px 8px' : '4px 12px',
      padding: isDesktopCollapsed ? '10px 0' : '10px 16px',
      transition: 'background-color 0.15s ease, color 0.15s ease, transform 0.15s ease',
      willChange: 'background-color, color, transform',
      fontSize: '14px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex',
      justifyContent: isDesktopCollapsed ? 'center' : 'flex-start',
      position: 'relative',
      '&:hover': {
        backgroundColor: active ? '#f0f7ff' : '#f8fafc',
        color: active ? '#2563eb' : '#1e293b',
        transform: isDesktopCollapsed ? 'scale(1.05)' : 'translateX(4px)',
      },
      ...(active && !isDesktopCollapsed && {
        '&::before': {
          content: '""',
          position: 'absolute',
          left: '-12px',
          top: '20%',
          height: '60%',
          width: '4px',
          backgroundColor: '#2563eb',
          borderRadius: '0 4px 4px 0',
        }
      })
    }),
    icon: ({ active }) => ({
      color: active ? '#2563eb' : '#64748b',
      fontSize: '20px',
      minWidth: isDesktopCollapsed ? '100%' : '24px',
      display: 'flex',
      justifyContent: 'center',
      margin: 0,
      transition: 'color 0.15s ease',
      willChange: 'color',
    }),
  }), [isDesktopCollapsed]);

  const submenuStyles = useMemo(() => ({
    button: ({ active }) => ({
      backgroundColor: 'transparent',
      color: active ? '#1e293b' : '#475569',
      fontWeight: active ? '600' : '500',
      borderRadius: '12px',
      margin: isDesktopCollapsed ? '4px 8px' : '4px 12px',
      padding: isDesktopCollapsed ? '10px 0' : '10px 16px',
      fontSize: '14px',
      display: 'flex',
      justifyContent: isDesktopCollapsed ? 'center' : 'flex-start',
      transition: 'background-color 0.15s ease, color 0.15s ease',
      willChange: 'background-color, color',
      '&:hover': {
        backgroundColor: '#f8fafc',
        color: '#1e293b',
      },
    }),
    content: {
      backgroundColor: 'transparent',
      paddingLeft: isDesktopCollapsed ? '0' : '16px',
    },
  }), [isDesktopCollapsed]);

  return (
    <>
      <div className="professional-sidebar" id="sidebar" data-collapsed={isDesktopCollapsed}>
        <Sidebar
          width="280px"
          collapsedWidth="60px"
          collapsed={!isMobile && isDesktopCollapsed}
          toggled={isMobile && isMobileOpen}
          onBackdropClick={toggleMobileMenu}
          onToggle={toggleMobileMenu}
          rootStyles={sidebarStyles}
        >
          <div className="sidebar-content-wrapper" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {isMobile && (
              <div className="mobile-sidebar-header">
                <img src={PrakurtiLogoWhite} alt="Logo" className="mobile-logo" />
                <button className="mobile-close-btn" onClick={toggleMobileMenu}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', willChange: 'transform', transform: 'translateZ(0)', paddingBottom: '120px' }} className="custom-scrollbar">              <Menu
              menuItemStyles={menuItemStyles}
              submenuStyles={submenuStyles}
            >
              {menuContent}
            </Menu>
            </div>
          </div>
        </Sidebar>
      </div>

      <style>{`
        .professional-sidebar {
          position: relative;
        }

        /* Header Logo Fix for Web View */
        .header-logo-img {
          width: 90px;
          height: 50px;
          object-fit: contain;
          transition: all 0.2s ease;
        }

        .mini-sidebar .header-logo-img {
          width: 40px;
          height: 30px;
        }

        .mini-sidebar .header-left {
          width: 60px !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }

        .menu-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          will-change: contents;
        }

        .menu-item-text {
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .mobile-sidebar-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px;
          border-bottom: 1px solid #edf2f7;
        }

        .mobile-logo {
          height: 30px;
          object-fit: contain;
        }

        .mobile-close-btn {
          background: #f8fafc;
          border: none;
          color: #64748b;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background-color 0.15s ease, color 0.15s ease;
          will-change: background-color, color;
        }

        .mobile-close-btn:hover {
          background: #f1f5f9;
          color: #1e293b;
        }

        .mobile-close-btn:active {
          transform: scale(0.95);
        }

        .sidebar-overlay.opened {
          backdrop-filter: blur(4px);
          background: rgba(0, 0, 0, 0.4) !important;
          display: block !important;
        }

        /* Hide submenu arrows when collapsed */
        .professional-sidebar[data-collapsed="true"] .ps-submenu-expand-icon {
          display: none !important;
        }

        .sidebar-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          color: #64748b;
        }

        .loading-spinner {
          width: 24px;
          height: 24px;
          border: 2px solid #e2e8f0;
          border-top: 2px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 12px;
          will-change: transform;
        }

        .sidebar-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          color: #ef4444;
        }

        .sidebar-error i {
          font-size: 24px;
          margin-bottom: 12px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
});

SidebarN.propTypes = {
  activeClassName: PropTypes.string,
  id: PropTypes.string,
  id1: PropTypes.string,
};

SidebarN.displayName = 'SidebarN';

export default SidebarN;
